import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { siteConfig } from "../data/mock";
import { ArrowRight } from "lucide-react";

export default function ChooserPage() {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 60);
    return () => clearTimeout(t);
  }, []);

  return (
    <main
      className="min-h-screen bg-[#FAFAF7] flex flex-col"
      data-testid="chooser-page"
    >
      {/* Top eyebrow with parent identity */}
      <header className="px-6 md:px-12 pt-8 md:pt-10">
        <div className="max-w-[1400px] mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="/logos/mods-symbol.png"
              alt="Mods Enterprises"
              className="h-10 md:h-11 w-auto"
              data-testid="chooser-parent-logo"
            />
            <span className="font-display text-xl md:text-2xl font-semibold text-stone-900 tracking-tight">
              Mods Enterprises
            </span>
          </div>
          <span className="hidden md:block font-body text-[11px] tracking-[0.28em] uppercase text-stone-400">
            Mods Enterprises, LLC · Est. Iowa
          </span>
        </div>
      </header>

      {/* Hero / chooser */}
      <section className="flex-1 flex flex-col justify-center px-6 md:px-12 py-16 md:py-24">
        <div className="max-w-[1400px] mx-auto w-full">
          <div
            className={`mb-12 md:mb-16 max-w-3xl transition-all duration-1000 ${
              mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
            }`}
          >
            <span className="font-body text-[11px] tracking-[0.32em] uppercase text-[#B8977E] mb-6 block">
              Two divisions · One studio
            </span>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl text-stone-900 leading-[1.04] tracking-tight">
              Building places people are proud to call home.
            </h1>
            <p className="font-body text-stone-500 text-base md:text-lg leading-relaxed mt-6 max-w-2xl">
              Mods Enterprises operates two complementary divisions — a custom home design studio and a
              long-hold rental real-estate portfolio. Choose where you'd like to begin.
            </p>
          </div>

          {/* Two cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {/* Catapult */}
            <Link
              to="/catapult"
              data-testid="chooser-catapult-card"
              className={`group relative overflow-hidden bg-stone-950 text-white p-10 md:p-14 min-h-[420px] md:min-h-[480px] flex flex-col justify-between transition-all duration-1000 hover:-translate-y-1 ${
                mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
              }`}
              style={{ transitionDelay: "200ms" }}
            >
              <div>
                <span className="font-body text-[11px] tracking-[0.32em] uppercase text-[#B8977E] mb-8 block">
                  Custom Homes · From {siteConfig.catapult.minBudget}
                </span>
                <h2 className="font-display text-4xl md:text-5xl text-white leading-[1.05] mb-6">
                  Catapult
                  <span className="block font-body text-[11px] tracking-[0.28em] uppercase text-white/50 mt-3">
                    Development &amp; Design
                  </span>
                </h2>
                <p className="font-body text-white/60 text-base leading-relaxed max-w-md">
                  An architectural design studio building one-of-one custom homes for clients who want their
                  home to reflect their life — not a catalog.
                </p>
              </div>
              <div className="flex items-center gap-3 font-body text-sm tracking-[0.12em] uppercase text-white pt-10">
                <span>Enter Catapult</span>
                <ArrowRight
                  size={18}
                  className="group-hover:translate-x-2 transition-transform duration-500"
                />
              </div>

              {/* subtle architectural line accent */}
              <div className="absolute inset-x-10 bottom-24 h-px bg-white/10 pointer-events-none" />
            </Link>

            {/* Mods Rentals */}
            <Link
              to="/mods"
              data-testid="chooser-mods-card"
              className={`group relative overflow-hidden bg-white text-stone-900 p-10 md:p-14 min-h-[420px] md:min-h-[480px] flex flex-col justify-between border border-stone-200 transition-all duration-1000 hover:-translate-y-1 ${
                mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
              }`}
              style={{ transitionDelay: "350ms" }}
            >
              <div>
                <span className="font-body text-[11px] tracking-[0.32em] uppercase text-[#B8977E] mb-8 block">
                  Real Estate · Rental Portfolios
                </span>
                <div className="flex items-center gap-4 mb-6">
                  <img
                    src="/logos/mods-symbol.png"
                    alt=""
                    className="h-12 md:h-14 w-auto"
                  />
                  <h2 className="font-display text-4xl md:text-5xl text-stone-900 leading-[1.05]">
                    Mods Enterprises
                  </h2>
                </div>
                <span className="block font-body text-[11px] tracking-[0.28em] uppercase text-stone-400 mb-6">
                  Rentals · Build-to-Rent · Investments
                </span>
                <p className="font-body text-stone-500 text-base leading-relaxed max-w-md">
                  A privately held portfolio of multi-family rentals, build-to-rent developments, and
                  joint-venture partnerships across central Iowa.
                </p>
              </div>
              <div className="flex items-center gap-3 font-body text-sm tracking-[0.12em] uppercase text-stone-900 pt-10">
                <span>Enter Mods</span>
                <ArrowRight
                  size={18}
                  className="group-hover:translate-x-2 transition-transform duration-500"
                />
              </div>
              <div className="absolute inset-x-10 bottom-24 h-px bg-stone-200 pointer-events-none" />
            </Link>
          </div>
        </div>
      </section>

      {/* Bottom strip */}
      <footer className="px-6 md:px-12 py-8 border-t border-stone-200">
        <div className="max-w-[1400px] mx-auto flex flex-col md:flex-row items-center justify-between gap-3">
          <p className="font-body text-[11px] tracking-[0.08em] text-stone-400">
            &copy; {new Date().getFullYear()} {siteConfig.parent.full}. All rights reserved.
          </p>
          <p className="font-body text-[11px] tracking-[0.2em] uppercase text-stone-400">
            Des Moines, Iowa
          </p>
        </div>
      </footer>
    </main>
  );
}
